import React from 'react';
import { Row, Col, Button, Form, Card } from 'react-bootstrap';
import { withFormik } from 'formik';
import * as Yup from 'yup';
import QuestionPreview from './PreviewLayouts/QuestionPreview';
import './survey.scss';
import { buildSliderAnswer } from './EvaluationScaleConfig';

const SurveyPreview = ({ survey, sendSurvey, isSubmitting, outOfDate, hideHeader, handleSubmit, isAnswered }) => (
  <Form onSubmit={handleSubmit}>
    <div className={hideHeader ? 'py-3' : 'yellow-wrapper'}>
      {!hideHeader && (
        <Col className="top-header-green br-5">
          <span className="card-header-title">{survey.name || 'Nueva Encuesta'}</span>
        </Col>
      )}

      <Card>
        <Card.Body>
          {survey.introText && (
            <p className="text-muted mb-2" style={{ whiteSpace: 'pre-wrap' }}>
              {survey.introText}
            </p>
          )}
          {survey.description && (
            <p className="text-muted mb-0" style={{ whiteSpace: 'pre-wrap' }}>
              {survey.description}
            </p>
          )}
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          {survey.questionsAttributes
            .filter(q => !q._destroy)
            .map((question, index) => (
              <QuestionPreview
                question={question}
                index={index}
                key={`question-${question.id || index}`}
                sendSurvey={sendSurvey}
                outOfDate={outOfDate || isAnswered}
              />
            ))}
        </Card.Body>
      </Card>

      {survey.endText && (
        <Card>
          <Card.Body>
            <p className="text-muted mb-0" style={{ whiteSpace: 'pre-wrap' }}>
              {survey.endText}
            </p>
          </Card.Body>
        </Card>
      )}
    </div>

    {sendSurvey && (
      <Row className="d-flex justify-content-end my-4">
        <Col md={2}>
          {outOfDate && !isAnswered && (
            <p className="text-danger text-center font-weight-bold">Encuesta fuera de plazo</p>
          )}
          {isAnswered && <p className="text-success text-center font-weight-bold">Encuesta respondida</p>}
          <Button type="submit" variant="primary" block disabled={isSubmitting || outOfDate || isAnswered}>
            Enviar
          </Button>
        </Col>
      </Row>
    )}
  </Form>
);

const buildInitialChoice = (question, rawChoices) => {
  const existing = rawChoices.filter(c => c.questionId === question.id && !c._destroy);

  let defaultAnswerText = '';
  if (
    question.questionType === 'evaluation_scale' &&
    ['points', 'semantic_differential'].includes(question.responseFormat)
  ) {
    const choices = question.questionChoicesAttributes || [];
    const rows = choices.filter(c => (c.kind === 'row' || c.kind === 'left') && !c._destroy);
    const map = {};
    rows.forEach(r => {
      map[r.position] = 0;
    });
    defaultAnswerText = buildSliderAnswer(map);
  } else if (
    question.questionType === 'structured' &&
    question.responseFormat === 'preference_order'
  ) {
    const choices = (question.questionChoicesAttributes || [])
      .filter(c => !c._destroy)
      .sort((a, b) => a.position - b.position);
    defaultAnswerText = choices.map(c => c.position).join(',');
  }

  const baseChoice = {
    questionId: question.id,
    answerText: defaultAnswerText,
    questionType: question.questionType,
    responseFormat: question.responseFormat,
    required: question.required,
    questionChoiceId: ''
  };

  if (question.responseFormat === 'multiple') {
    return {
      ...baseChoice,
      questionChoiceId: existing.map(c => String(c.questionChoiceId)),
      originalIds: existing.map(c => ({ id: c.id, choiceId: String(c.questionChoiceId) }))
    };
  }

  if (existing.length > 0) {
    const [firstChoice] = existing;
    return {
      ...baseChoice,
      id: firstChoice.id,
      questionChoiceId: firstChoice.questionChoiceId ? String(firstChoice.questionChoiceId) : '',
      answerText: firstChoice.answerText || ''
    };
  }

  return baseChoice;
};

const setInitialValues = ({ employeeSurvey, survey }) => {
  const rawChoices = employeeSurvey?.employeeQuestionChoicesAttributes || [];

  const initialChoices = (survey?.questionsAttributes || [])
    .filter(q => !q._destroy)
    .map(q => buildInitialChoice(q, rawChoices));

  return {
    employeeSurvey: {
      ...employeeSurvey,
      status: 'answered',
      employeeQuestionChoicesAttributes: initialChoices
    }
  };
};

const validationSchema = Yup.object().shape({
  employeeSurvey: Yup.object().shape({
    employeeQuestionChoicesAttributes: Yup.array().of(
      Yup.object().shape({
        questionType: Yup.string(),
        responseFormat: Yup.string(),
        required: Yup.boolean(),
        questionChoiceId: Yup.mixed().when(['questionType', 'responseFormat', 'required'], {
          is: (type, format, req) => {
            if (!req) return false;
            if (type === 'open') return false;
            if (type === 'structured') return false;
            if (type === 'evaluation_scale' && ['points', 'semantic_differential'].includes(format)) return false;
            return true;
          },
          then: schema =>
            schema.test('is-not-empty', 'Debes seleccionar al menos una opción', value => {
              if (Array.isArray(value)) return value.length > 0;
              return value !== null && value !== undefined && String(value).trim() !== '';
            }),
          otherwise: schema => schema.nullable()
        }),
        answerText: Yup.string().when(['questionType', 'responseFormat', 'required'], {
          is: (type, format, req) => {
            if (!req) return false;
            if (type === 'open') return true;
            if (type === 'structured') return true;
            if (type === 'evaluation_scale' && ['points', 'semantic_differential'].includes(format)) return true;
            return false;
          },
          then: schema => schema.required('Debes responder esta pregunta'),
          otherwise: schema => schema.nullable()
        }),
        questionId: Yup.string().nullable()
      })
    )
  })
});

const transformMultipleChoice = attr => {
  const selectedIds = attr.questionChoiceId;
  const originalIds = attr.originalIds || [];

  const selectedAttributes = selectedIds.map(choiceId => {
    const original = originalIds.find(o => o.choiceId === String(choiceId));
    return {
      id: original ? original.id : null,
      questionId: attr.questionId,
      questionChoiceId: choiceId
    };
  });

  const destroyedAttributes = originalIds
    .filter(o => !selectedIds.includes(o.choiceId))
    .map(o => ({
      id: o.id,
      questionId: attr.questionId,
      _destroy: true
    }));

  return [...selectedAttributes, ...destroyedAttributes];
};

const transformChoiceAttribute = attr => {
  if (Array.isArray(attr.questionChoiceId)) {
    return transformMultipleChoice(attr);
  }

  return [
    {
      id: attr.id,
      questionId: attr.questionId,
      questionChoiceId: attr.questionChoiceId,
      answerText: attr.answerText
    }
  ];
};

const handleSubmit = (values, { props, setSubmitting }) => {
  const { formRequest, sendSurvey } = props;

  if (!sendSurvey) return;

  const transformedAttributes = values.employeeSurvey.employeeQuestionChoicesAttributes.flatMap(
    transformChoiceAttribute
  );

  const transformedValues = {
    ...values,
    employeeSurvey: {
      ...values.employeeSurvey,
      employeeQuestionChoicesAttributes: transformedAttributes
    }
  };

  formRequest(transformedValues, setSubmitting);
};

export default withFormik({
  mapPropsToValues: setInitialValues,
  validationSchema,
  handleSubmit,
  enableReinitialize: true
})(SurveyPreview);
